import { Icons } from '@pankod/refine-antd';
import React, { ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import { MobileActionConfirmModal } from './mobile-action-confirm-modal';
import styles from './mobile-data-card.module.css';

type Money = number | null | undefined;

type OpenHandle = { id: string; close: () => void };
let openHandle: OpenHandle | null = null;

type ActionItem = {
  key: string;
  icon: ReactNode;
  label: ReactNode;
  onClick?: () => void | Promise<void>;
  danger?: boolean;
  disabled?: boolean;
  confirm?: {
    title?: ReactNode;
    okText?: string;
    cancelText?: string;
  };
};

type Props = {
  // media
  image?: ReactNode; // <img .../>

  // text
  title: ReactNode; // big title
  skuText?: ReactNode; // fallback: "SKU: RV-1003L"
  skuValue?: ReactNode; // renders as "SKU: {skuValue} [copy]"
  skuCopyText?: string; // copies this value to clipboard
  secondaryText?: ReactNode; // e.g. "Units: 12"

  // pricing
  sellingPrice?: Money; // 14
  currencySymbol?: string; // default "TK"

  // actions
  actions?: ActionItem[];
};

function formatPrice(value: Money, currency: string) {
  if (value === null || value === undefined || Number.isNaN(Number(value))) return '—';
  const n = Number(value);
  return `${currency} ${n.toFixed(2)}`;
}

export const MobileDataCard: React.FC<Props> = ({
  image,
  title,
  skuText,
  skuValue,
  skuCopyText,
  secondaryText,
  sellingPrice,
  currencySymbol = 'TK',
  actions = [],
}) => {
  const [isSkuCopied, setIsSkuCopied] = useState(false);
  const [isActionsOpen, setIsActionsOpen] = useState(false);
  const [confirmAction, setConfirmAction] = useState<ActionItem | null>(null);
  const copyResetTimerRef = useRef<number | null>(null);

  const instanceIdRef = useRef(`mdc_${Math.random().toString(36).slice(2)}`);

  const closeActions = useCallback(() => {
    setIsActionsOpen(false);
    if (openHandle?.id === instanceIdRef.current) {
      openHandle = null;
    }
  }, []);

  const openActions = useCallback(() => {
    if (openHandle?.id && openHandle.id !== instanceIdRef.current) {
      openHandle.close();
    }
    openHandle = { id: instanceIdRef.current, close: closeActions };
    setIsActionsOpen(true);
  }, [closeActions]);

  useEffect(() => {
    const instanceId = instanceIdRef.current;

    return () => {
      if (openHandle?.id === instanceId) {
        openHandle = null;
      }
    };
  }, []);

  const toggleActions = useCallback(() => {
    if (!actions.length) return;
    if (isActionsOpen) {
      closeActions();
    } else {
      openActions();
    }
  }, [actions.length, closeActions, isActionsOpen, openActions]);

  const copySku = useCallback(async () => {
    if (!skuCopyText) {
      return;
    }

    const setCopiedUi = () => {
      setIsSkuCopied(true);
      if (copyResetTimerRef.current) {
        window.clearTimeout(copyResetTimerRef.current);
      }
      copyResetTimerRef.current = window.setTimeout(() => {
        setIsSkuCopied(false);
        copyResetTimerRef.current = null;
      }, 1200);
    };

    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(skuCopyText);
        setCopiedUi();
        return;
      }
    } catch {
      // ignore and fallback
    }

    try {
      const textarea = document.createElement('textarea');
      textarea.value = skuCopyText;
      textarea.setAttribute('readonly', 'true');
      textarea.style.position = 'fixed';
      textarea.style.left = '-9999px';
      textarea.style.top = '0';
      document.body.appendChild(textarea);
      textarea.select();
      const ok = document.execCommand('copy');
      document.body.removeChild(textarea);
      if (ok) {
        setCopiedUi();
      }
    } catch {
      // ignore
    }
  }, [skuCopyText]);

  const hasSku = Boolean(skuValue || skuText);
  const isConfirmOpen = Boolean(confirmAction?.confirm);
  const skuTextValue =
    typeof skuValue === 'string'
      ? skuValue
      : typeof skuCopyText === 'string'
        ? skuCopyText
        : undefined;

  return (
    <article
      className={styles.card}
      role={actions.length ? 'button' : undefined}
      tabIndex={actions.length ? 0 : undefined}
      aria-label={actions.length ? (isActionsOpen ? 'Hide actions' : 'Show actions') : undefined}
      aria-expanded={actions.length ? isActionsOpen : undefined}
      onClick={(event) => {
        if (!actions.length) return;
        event.stopPropagation();
        toggleActions();
      }}
      onKeyDown={(event) => {
        if (!actions.length) return;
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          event.stopPropagation();
          toggleActions();
        }
        if (event.key === 'Escape') {
          event.preventDefault();
          event.stopPropagation();
          closeActions();
        }
      }}
    >
      <div className={styles.media}>{image}</div>

      <div className={styles.body}>
        <div className={styles.title}>{title}</div>

        {hasSku ? (
          <div className={styles.skuRow}>
            {skuValue ? (
              <div className={styles.skuMain}>
                <div className={styles.sku}>
                  <span className={styles.skuPrefix}>SKU:</span>
                  <span className={styles.skuValue}>{skuValue}</span>
                  {skuCopyText ? (
                    <button
                      type="button"
                      className={styles.skuCopyInlineBtn}
                      aria-label={isSkuCopied ? 'Copied' : 'Copy SKU'}
                      onClick={(event) => {
                        event.stopPropagation();
                        copySku();
                      }}
                    >
                      <span className={styles.skuCopyIcon}>
                        {isSkuCopied ? <Icons.CheckOutlined /> : <Icons.CopyOutlined />}
                      </span>
                    </button>
                  ) : null}
                </div>
              </div>
            ) : (
              <div className={styles.skuMain}>
                <div className={styles.sku}>{skuText}</div>
              </div>
            )}

            {secondaryText ? <div className={styles.secondaryText}>{secondaryText}</div> : null}
          </div>
        ) : null}

        {secondaryText && !hasSku ? (
          <div className={styles.secondaryTextStandalone}>{secondaryText}</div>
        ) : null}

        <div className={styles.footerRow}>
          <div className={styles.selling}>{formatPrice(sellingPrice, currencySymbol)}</div>

          {actions.length ? (
            <button
              type="button"
              className={styles.actionToggle}
              aria-label={isActionsOpen ? 'Hide actions' : 'Show actions'}
              onClick={(event) => {
                event.stopPropagation();
                toggleActions();
              }}
            >
              <span className={styles.actionToggleIcon}>
                {isActionsOpen ? <Icons.CloseOutlined /> : <Icons.MoreOutlined />}
              </span>
            </button>
          ) : null}
        </div>
      </div>

      <div className={styles.actionArea} aria-hidden={!isActionsOpen}>
        {actions.length ? (
          <div
            className={[styles.actionTray, isActionsOpen ? styles.actionTrayOpen : ''].join(' ')}
          >
            {actions.map((a) =>
              a.confirm ? (
                <button
                  key={a.key}
                  type="button"
                  disabled={!!a.disabled}
                  className={[styles.actionBtn, a.danger ? styles.actionDanger : ''].join(' ')}
                  aria-label={typeof a.label === 'string' ? a.label : undefined}
                  onClick={(event) => {
                    event.stopPropagation();
                    setConfirmAction(a);
                    closeActions();
                  }}
                >
                  <span className={styles.actionIcon}>{a.icon}</span>
                  <span className={styles.actionLabel}>{a.label}</span>
                </button>
              ) : (
                <button
                  key={a.key}
                  type="button"
                  disabled={!!a.disabled}
                  className={[styles.actionBtn, a.danger ? styles.actionDanger : ''].join(' ')}
                  aria-label={typeof a.label === 'string' ? a.label : undefined}
                  onClick={(event) => {
                    event.stopPropagation();
                    a.onClick?.();
                    closeActions();
                  }}
                >
                  <span className={styles.actionIcon}>{a.icon}</span>
                  <span className={styles.actionLabel}>{a.label}</span>
                </button>
              ),
            )}
          </div>
        ) : null}
      </div>

      <MobileActionConfirmModal
        open={isConfirmOpen}
        itemName={title}
        sku={skuTextValue}
        action={confirmAction}
        onConfirm={async () => {
          const a = confirmAction;
          if (!a) return;
          await a.onClick?.();
          setConfirmAction(null);
        }}
        onCancel={() => setConfirmAction(null)}
      />
    </article>
  );
};
